import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Box, Typography, Paper, Button, TextField, Alert, CircularProgress } from '@mui/material';
import ShareButtons from '../components/common/ShareButtons';

function MyFoundItemsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const fetchItems = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get('/api/items/my-found', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(data);
      } catch (err) {
        setError("Failed to fetch items.");
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [token]);

  const startEdit = (item) => {
    setEditId(item._id);
    setEditForm({
      itemName: item.itemName,
      description: item.description,
      extraPoints: item.extraPoints.join(", "),
      phone: item.phone,
      whatsapp: item.whatsapp,
      email: item.email,
    });
  };

  const saveEdit = async (id) => {
    try {
      const updatedData = {
        ...editForm,
        extraPoints: editForm.extraPoints.split(",").map(s => s.trim()).filter(Boolean),
      };
      await axios.patch(`/api/items/found/${id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(items.map(item => item._id === id ? { ...item, ...updatedData, extraPoints: updatedData.extraPoints } : item));
      setEditId(null);
      setToast("Item updated successfully!");
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      console.error("Failed to update item", err);
      setError("Failed to update item.");
    }
  };

  const deleteItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await axios.delete(`/api/items/found/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(items.filter(item => item._id !== id));
        setToast("Item deleted successfully!");
        setTimeout(() => setToast(""), 3000);
      } catch (err) {
        console.error("Failed to delete item", err);
        setError("Failed to delete item.");
      }
    }
  };

  const markAsResolved = async (id) => {
    if (window.confirm("Are you sure this item has been resolved? This action cannot be undone.")) {
      try {
        await axios.patch(`/api/items/${id}/status`, { status: 'resolved' }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(items.map(item => item._id === id ? { ...item, status: 'resolved' } : item));
        setToast("Item marked as resolved!");
        setTimeout(() => setToast(""), 3000);
      } catch (err) {
        setError("Failed to update item status.");
      }
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>My Found Items</Typography>
      {toast && <Alert severity="success" sx={{ mb: 2 }}>{toast}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.length === 0 && !loading && <Typography>You have not posted any found items yet.</Typography>}
      {items.map(item => (
        <Paper key={item._id} sx={{ p: 2, mb: 2, opacity: item.status === 'resolved' ? 0.5 : 1 }}>
          {editId === item._id ? (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); saveEdit(item._id); }}>
              <TextField label="Item Name" fullWidth margin="normal" value={editForm.itemName} onChange={(e) => setEditForm({...editForm, itemName: e.target.value})} />
              <TextField label="Description" fullWidth margin="normal" value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} />
              <TextField label="Extra Points (comma separated)" fullWidth margin="normal" value={editForm.extraPoints} onChange={(e) => setEditForm({...editForm, extraPoints: e.target.value})} />
              <TextField label="Phone" fullWidth margin="normal" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} />
              <TextField label="WhatsApp" fullWidth margin="normal" value={editForm.whatsapp} onChange={(e) => setEditForm({...editForm, whatsapp: e.target.value})} />
              <TextField label="Email" fullWidth margin="normal" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
              <Button type="submit" variant="contained">Save</Button>
              <Button onClick={() => setEditId(null)} sx={{ ml: 1 }}>Cancel</Button>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography variant="h6">{item.itemName}</Typography>
                  <Typography>Description: {item.description}</Typography>
                  {item.image && <img src={item.image} alt={item.itemName} style={{ maxWidth: 100, maxHeight: 100, marginTop: 8 }} />}
                </Box>
                <ShareButtons item={item} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button onClick={() => startEdit(item)} disabled={item.status === 'resolved'}>Edit</Button>
                <Button onClick={() => deleteItem(item._id)} color="error" disabled={item.status === 'resolved'}>Delete</Button>
                {item.status !== 'resolved' && (
                  <Button onClick={() => markAsResolved(item._id)} color="success">Mark as Resolved</Button>
                )}
                {item.status === 'resolved' && (
                  <Typography color="success.main" sx={{ display: 'inline', ml: 2, fontWeight: 'bold' }}>RESOLVED</Typography>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      ))}
      <Button variant="outlined" onClick={() => navigate('/my-items-menu')}>Back</Button>
    </Box>
  );
}

export default MyFoundItemsPage;
